import { Stack } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { PageHeader } from '@/components/navigation/PageHeader';
import {
  StepTracker,
  StepTrackerStatus,
} from '@/components/navigation/StepTracker/StepTracker';
import { ErrorFeedbackFallback } from '@/components/notifications/Feedback/ErrorFeedbackFallback';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { DeleteProposalMenuItem } from '@/modules/proposal/components/DeleteProposalMenuItem/DeleteProposalMenuItem';
import { ProposalDetailsAwareRoute } from '@/modules/proposal/contexts/ProposalDetailsAwareRoute';
import { NEW_PROPOSAL_ID } from '@/modules/proposal/proposal.constants';
import { ProposalKind } from '@/modules/proposal/types/ProposalKind';
import { CharitableTrustDesignerStages } from '@/navigation/constants';
import { CONFIRMATION_MESSAGE } from '@/navigation/UnloadPrompt.provider';
// eslint-disable-next-line luminary-rules/no-page-imports
import { CharitableTrustDesignerAnalysis } from '@/pages/designer/CharitableTrustDesigner/CharitableTrustDesignerAnalysis/CharitableTrustDesignerAnalysis';
// eslint-disable-next-line luminary-rules/no-page-imports
import { CharitableTrustDesignerBasicInformation } from '@/pages/designer/CharitableTrustDesigner/CharitableTrustDesignerBasicInformation/CharitableTrustDesignerBasicInformation';
// eslint-disable-next-line luminary-rules/no-page-imports
import {
  CharitableTrustDesignerProvider,
  useCharitableTrustDesignerContext,
  useIsCRT,
} from '@/pages/designer/CharitableTrustDesigner/CharitableTrustDesignerContext';
import { COLORS } from '@/styles/tokens/colors';

const StackWrapper = ({ children }: PropsWithChildren) => (
  <Stack height={`100vh`}>{children}</Stack>
);

interface CharitableTrustDesignerInnerProps {
  isOpen: boolean;
  onFormAction: (
    action: 'cancel' | CharitableTrustDesignerStages,
    crtId?: string
  ) => void;
}

function CharitableTrustDesignerInner({
  isOpen,
  onFormAction,
}: CharitableTrustDesignerInnerProps) {
  const { variant, proposalId } = useCharitableTrustDesignerContext();
  const isCRT = useIsCRT();
  const canDeleteProposal = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_DELETE_ENTITIES_AND_PROPOSALS
  );

  const [stage, setStage] = useState<CharitableTrustDesignerStages>(
    CharitableTrustDesignerStages.BASIC_INFORMATION
  );

  const [isDirty, setIsDirty] = useState(false);

  const isNewProposal = proposalId === NEW_PROPOSAL_ID;
  const kind: ProposalKind = isCRT ? ProposalKind.CRT : ProposalKind.CLT;
  const shouldBlock = isDirty && isOpen;

  const handleFormAction = useCallback(
    (action: 'cancel' | CharitableTrustDesignerStages, crtId?: string) => {
      if (shouldBlock && !crtId) {
        // Block if we are not saving
        const isConfirmed = window.confirm?.(CONFIRMATION_MESSAGE);

        if (!isConfirmed) {
          return;
        }
      }

      if (action === CharitableTrustDesignerStages.BASIC_INFORMATION) {
        setStage(CharitableTrustDesignerStages.MODEL_SCENARIOS);
      } else if (
        action === CharitableTrustDesignerStages.MODEL_SCENARIOS &&
        !crtId
      ) {
        // If we are on the model scenarios stage and we do not have a crtId, we go back to the basic information stage
        setStage(CharitableTrustDesignerStages.BASIC_INFORMATION);
      }

      onFormAction(action, crtId);
    },
    [onFormAction, shouldBlock]
  );

  const Stage = useMemo(() => {
    switch (stage) {
      case CharitableTrustDesignerStages.BASIC_INFORMATION:
        return (
          <CharitableTrustDesignerBasicInformation
            onFormAction={handleFormAction}
            onDirtyChange={setIsDirty}
          />
        );
      case CharitableTrustDesignerStages.MODEL_SCENARIOS:
        return (
          <CharitableTrustDesignerAnalysis
            onFormAction={handleFormAction}
            onDirtyChange={setIsDirty}
          />
        );
    }
  }, [handleFormAction, stage]);

  const showContextMenu = !!proposalId && canDeleteProposal;

  return (
    <StackWrapper>
      <PageHeader
        heading={`Model a ${variant.toUpperCase()} analysis`}
        action={
          <Stack
            direction="row"
            alignItems="center"
            alignContent="center"
            spacing={2}
          >
            <StepTracker
              segments={[
                {
                  display: 'Basic information',
                  status:
                    stage === CharitableTrustDesignerStages.BASIC_INFORMATION ||
                    stage === CharitableTrustDesignerStages.MODEL_SCENARIOS
                      ? StepTrackerStatus.Active
                      : StepTrackerStatus.Inactive,
                  onClick: () =>
                    setStage(CharitableTrustDesignerStages.BASIC_INFORMATION),
                },
                {
                  display: 'Analysis illustration',
                  status:
                    stage === CharitableTrustDesignerStages.MODEL_SCENARIOS
                      ? StepTrackerStatus.Active
                      : StepTrackerStatus.Inactive,
                  onClick: () => {
                    if (!isNewProposal) {
                      setStage(CharitableTrustDesignerStages.MODEL_SCENARIOS);
                    }
                  },
                },
              ]}
              activeSegmentIndex={[
                CharitableTrustDesignerStages.BASIC_INFORMATION,
                CharitableTrustDesignerStages.MODEL_SCENARIOS,
              ].indexOf(stage)}
            />
            {showContextMenu && (
              <ContextMenuButton variant="secondary" disabled={isNewProposal}>
                <DeleteProposalMenuItem
                  proposalId={proposalId}
                  kind={kind}
                  onDelete={() => {
                    onFormAction('cancel');
                  }}
                />
              </ContextMenuButton>
            )}
          </Stack>
        }
      />
      {Stage}
    </StackWrapper>
  );
}

interface CharitableTrustDesignerTrowserProps {
  isOpen: boolean;
  householdId: string;
  proposalId: string;
  variant: 'crt' | 'clt';
  onFormAction: (action: 'cancel' | CharitableTrustDesignerStages) => void;
}

export function CharitableTrustDesignerTrowser({
  isOpen,
  householdId,
  proposalId,
  variant,
  onFormAction,
}: CharitableTrustDesignerTrowserProps) {
  return (
    <FullScreenModal isOpen={isOpen} backdropSx={{ bgcolor: COLORS.GRAY[50] }}>
      <ErrorBoundary fallback={<ErrorFeedbackFallback />}>
        <ProposalDetailsAwareRoute proposalId={proposalId}>
          <CharitableTrustDesignerProvider
            variant={variant}
            householdId={householdId}
            proposalId={proposalId}
          >
            <CharitableTrustDesignerInner
              isOpen={isOpen}
              onFormAction={onFormAction}
            />
          </CharitableTrustDesignerProvider>
        </ProposalDetailsAwareRoute>
      </ErrorBoundary>
    </FullScreenModal>
  );
}
